import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import type { Subscription } from "../../Graphql/types/subscription";
import CancelSubscriptionModal from "./CancelSubscriptionModal";
import ActivateSubscriptionModal from "./ActivateSubscriptionModal";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";

// --- Helpers ---

const formatSubscriptionDate = (dateString: string): string =>
  new Date(dateString).toLocaleDateString();

const formatStatusLabel = (status: string): string =>
  status.charAt(0) + status.slice(1).toLowerCase().replace(/_/g, " ");

const STATUS_COLOR: Record<
  string,
  "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning"
> = {
  ACTIVE: "success",
  PENDING: "default",
  PAST_DUE: "warning",
  EXPIRED: "error",
  CANCELLED: "default",
  PAUSED: "warning"
};

const INACTIVE_HEADER_GRADIENT =
  "linear-gradient(90deg, #9e9e9e 0%, #616161 100%)";

const STATUS_GRADIENT: Record<string, string> = {
  ACTIVE: "linear-gradient(90deg, #17802b 0%, #2dac37 100%)",
  PENDING: "linear-gradient(90deg, #78909c 0%, #546e7a 100%)",
  PAST_DUE: "linear-gradient(90deg, #f093fb 0%, #f5576c 100%)",
  EXPIRED: "linear-gradient(90deg, #fa709a 0%, #fee140 100%)",
  CANCELLED: INACTIVE_HEADER_GRADIENT,
  PAUSED: INACTIVE_HEADER_GRADIENT
};

const STATUS_BADGE_BG: Record<string, string> = {
  ACTIVE: "#2dac37",
  PAUSED: "#ffc107",
  CANCELLED: "#757575",
  PENDING: "#bdbdbd",
  PAST_DUE: "#f5576c",
  EXPIRED: "#e53935"
};

const getStatusColor = (status: string) => STATUS_COLOR[status] ?? "default";

const getStatusGradient = (status: string) =>
  STATUS_GRADIENT[status] ?? "linear-gradient(90deg, #bdc3c7 0%, #95a5a6 100%)";

const getStatusBadgeBg = (status: string) =>
  STATUS_BADGE_BG[status] ?? "#757575";

// --- Subcomponents ---

interface DetailRowProps {
  label: string;
  value: string;
}

const DetailRow: React.FC<DetailRowProps> = ({ label, value }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight="medium">
      {value}
    </Typography>
  </Box>
);

// --- Main component ---

interface SubscriptionCardProps {
  subscription: Subscription;
  planName?: string;
  isPrimary?: boolean;
  features?: Array<{ name: string; included: boolean }>;
  onCancel?: (subscription: Subscription) => void;
}

const SubscriptionCard: React.FC<SubscriptionCardProps> = ({
  subscription,
  planName,
  isPrimary = false,
  features = [],
  onCancel
}) => {
  const { t } = useKivunovaTranslation();
  const [openCancelModal, setOpenCancelModal] = React.useState(false);
  const [openActivateModal, setOpenActivateModal] = React.useState(false);

  const isTrial = !subscription.billingCycleId;
  const daysRemaining = Math.ceil(
    (new Date(subscription.subscriptionEndsAt).getTime() - Date.now()) /
      (1000 * 60 * 60 * 24)
  );

  const displayName =
    planName || subscription.planName || `Plan ${subscription.planId}`;
  const statusLabel = isPrimary
    ? t("subscription-card-status-active", "Active")
    : formatStatusLabel(subscription.status);
  const chipColor =
    STATUS_BADGE_BG[subscription.status] != null
      ? undefined
      : getStatusColor(subscription.status);
  const badgeBg = getStatusBadgeBg(subscription.status);
  const badgeTextColor =
    subscription.status === "PENDING" ? "#424242" : "white";

  return (
    <>
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: isPrimary
            ? "0 15px 50px rgba(45, 172, 55, 0.3)"
            : "0 10px 40px rgba(0,0,0,0.1)",
          overflow: "visible",
          position: "relative",
          border: isPrimary ? "2px solid #2dac37" : "none",
          transform: isPrimary ? "scale(1.02)" : "scale(1)",
          transition: "all 0.3s ease",
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column"
        }}
      >
        {/* Status Badge */}
        <Box sx={{ position: "absolute", top: -12, right: 20, zIndex: 1 }}>
          <Chip
            label={statusLabel}
            {...(chipColor ? { color: chipColor } : {})}
            sx={{
              fontWeight: "bold",
              boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
              bgcolor: badgeBg,
              color: badgeTextColor,
              ...(subscription.status === "ACTIVE" && {
                border: "1px solid #17802b"
              }),
              ...(subscription.status === "CANCELLED" && {
                border: "1px solid #424242"
              }),
              ...(subscription.status === "PENDING" && {
                border: "1px solid #9e9e9e"
              }),
              ...(subscription.status === "PAUSED" && {
                border: "1px solid #fbc02d"
              })
            }}
          />
        </Box>

        {/* Header */}
        <Box
          sx={{
            background: getStatusGradient(subscription.status),
            color: "white",
            p: 4,
            borderRadius: "12px 12px 0 0",
            position: "relative"
          }}
        >
          <Typography
            variant="h4"
            fontWeight="bold"
            sx={{ mb: 1, fontSize: "1.25rem", lineHeight: 1.25 }}
          >
            {displayName}
          </Typography>
          {isTrial || subscription.priceAmount === 0 ? (
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              {subscription.priceAmount === 0
                ? t("subscription-card-free-tier", "Free tier")
                : t(
                    "subscription-card-free-trial-days",
                    "Free trial - {{daysRemaining}} days remaining",
                    { daysRemaining: String(daysRemaining) }
                  )}
            </Typography>
          ) : (
            <Typography
              variant="body1"
              fontWeight="bold"
              sx={{ fontSize: "14px" }}
            >
              {subscription.priceCurrency}{" "}
              {subscription.priceAmount?.toLocaleString()}
              <Typography
                component="span"
                variant="body2"
                sx={{ ml: 1, opacity: 0.9, fontSize: "14px" }}
              >
                {t("subscription-card-per-month", "/ month")}
              </Typography>
            </Typography>
          )}
        </Box>

        <CardContent
          sx={{ p: 4, display: "flex", flexDirection: "column", flexGrow: 1 }}
        >
          {/* Details */}
          <Box sx={{ mb: 3 }}>
            <DetailRow
              label={t("subscription-card-start-date", "Start Date")}
              value={formatSubscriptionDate(subscription.subscriptionStartsAt)}
            />
            <DetailRow
              label={t("subscription-card-end-date", "End Date")}
              value={formatSubscriptionDate(subscription.subscriptionEndsAt)}
            />
            {subscription.nextBillingAt && (
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">
                  {t("subscription-card-next-billing", "Next Billing")}
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formatSubscriptionDate(subscription.nextBillingAt)}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Features */}
          {features.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ mb: 2, flexGrow: 1 }}>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{ mb: 1.5, color: "text.primary" }}
                >
                  {t(
                    "subscription-card-features-included",
                    "Features included:"
                  )}
                </Typography>
                <List dense sx={{ py: 0 }}>
                  {features.map((feature, index) => (
                    <ListItem key={index} sx={{ py: 0.25, px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 24 }}>
                        {feature.included ? (
                          <CheckCircleIcon
                            sx={{ color: "#4caf50", fontSize: "1rem" }}
                          />
                        ) : (
                          <CancelIcon
                            sx={{ color: "#f44336", fontSize: "1rem" }}
                          />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={feature.name}
                        primaryTypographyProps={{
                          variant: "body2",
                          color: "text.secondary",
                          sx: {
                            fontSize: "0.75rem",
                            textDecoration: feature.included
                              ? "none"
                              : "line-through",
                            opacity: feature.included ? 1 : 0.6
                          }
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Cancel warning */}
          {subscription.cancelAtPeriodEnd && (
            <Box
              sx={{
                p: 2,
                bgcolor: "#ff9800",
                borderRadius: 2,
                mb: 3,
                border: "2px solid #f57c00"
              }}
            >
              <Typography
                variant="body2"
                color="white"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  fontSize: "0.875rem"
                }}
              >
                ⚠️{" "}
                {t(
                  "subscription-card-cancel-warning",
                  "This subscription will cancel on"
                )}{" "}
                {formatSubscriptionDate(subscription.subscriptionEndsAt)}
              </Typography>
            </Box>
          )}

          {/* Actions */}
          {subscription.status === "ACTIVE" &&
            !subscription.cancelAtPeriodEnd &&
            !isPrimary && (
              <Button
                fullWidth
                variant="outlined"
                color="error"
                onClick={() => setOpenCancelModal(true)}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: "bold",
                  textTransform: "none"
                }}
              >
                {t("subscription-card-cancel-button", "Cancel subscription")}
              </Button>
            )}
          {isPrimary && (
            <Box
              sx={{
                p: 2,
                bgcolor: "primary.light",
                borderRadius: 2,
                textAlign: "center"
              }}
            >
              <Typography
                variant="body2"
                color="primary.dark"
                sx={{ fontSize: "0.875rem" }}
              >
                ✓{" "}
                {t(
                  "subscription-card-active-indicator",
                  "This is your active subscription"
                )}
              </Typography>
            </Box>
          )}
          {subscription.status === "PENDING" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={() => setOpenActivateModal(true)}
                sx={{
                  backgroundColor: BRAND_COLOR,
                  "&:hover": { backgroundColor: BRAND_HOVER },
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: "bold",
                  textTransform: "none"
                }}
              >
                {t("subscription-card-activate-button", "Activate")}
              </Button>
              <Button
                fullWidth
                variant="outlined"
                color="error"
                onClick={() => setOpenCancelModal(true)}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: "bold",
                  textTransform: "none"
                }}
              >
                {t("subscription-card-cancel-button", "Cancel")}
              </Button>
            </Box>
          )}
          {subscription.status === "EXPIRED" && (
            <Button
              fullWidth
              variant="contained"
              sx={{
                backgroundColor: BRAND_COLOR,
                "&:hover": { backgroundColor: BRAND_HOVER },
                py: 1.5,
                borderRadius: 2,
                fontWeight: "bold",
                textTransform: "none"
              }}
            >
              {t("subscription-card-renew-button", "Renew subscription")}
            </Button>
          )}
        </CardContent>
      </Card>

      <ActivateSubscriptionModal
        open={openActivateModal}
        onClose={() => setOpenActivateModal(false)}
        subscription={subscription}
      />
      {(onCancel || subscription.status === "PENDING") && (
        <CancelSubscriptionModal
          open={openCancelModal}
          onClose={() => setOpenCancelModal(false)}
          subscription={subscription}
          onSuccess={() => onCancel?.(subscription)}
        />
      )}
    </>
  );
};

export default SubscriptionCard;
